import React, { useState, useMemo } from 'react';
import { useMCP } from '../../context/McpProvider';
import { DataState } from '../../context/DataProvider';
import { IoMdClose } from 'react-icons/io';
import { BsPuzzle } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FiAlertTriangle } from 'react-icons/fi';
import { CgPlug } from 'react-icons/cg';

// A reusable toggle switch component for the UI
// A reusable, theme-aligned, and accessible toggle switch component.
const ToggleSwitch = ({ enabled, onChange, disabled = false }) => {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`
        relative inline-flex items-center h-6 rounded-full w-11 
        transition-colors duration-200 ease-in-out 
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900 focus:ring-[#5652CF]
        disabled:opacity-50 disabled:cursor-not-allowed
        ${enabled ? 'bg-blue-600' : 'bg-zinc-700'} 
      `}
    >
      <span
        className={`
          inline-block w-4 h-4 transform rounded-full bg-white 
          transition-transform duration-200 ease-in-out
          ${enabled ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  );
};

const McpTaskSelector = ({ isOpen, onClose }) => {
  // Global state: All available connections and tools from the main context
  const { connections, oqlousTools, isLoading } = useMCP();

  // Task-specific state: What the user has selected for the current task
  const { taskMCPConnections, setTaskMCPConnections,
    taskOqlousTools, setTaskOqlousTools, taskManualToolSelection,
    setTaskManualToolSelection } = DataState();

  // Local UI state for managing which accordion is open
  const [expandedMcpId, setExpandedMcpId] = useState(null); // For MCP Connections
  const [expandedAppName, setExpandedAppName] = useState(null); // For Oqlous Apps

  // Memoized logic to efficiently partition all connections and apps into valid and invalid lists
  const {
    validConnectionsToShow,
    invalidatedConnectionsToShow,
    validOqlousAppsToShow,
    invalidatedOqlousAppsToShow
  } = useMemo(() => {
    if (isLoading) {
      return { validConnectionsToShow: [], invalidatedConnectionsToShow: [], validOqlousAppsToShow: [], invalidatedOqlousAppsToShow: [] };
    }

    // --- Partition MCP Connections ---
    const invalidatedMcp = taskMCPConnections.map(taskConn => {
      const globalConn = connections.find(c => c.id === taskConn.id);
      if (!globalConn || globalConn.connection_status !== 'valid') {
        return {
          id: taskConn.id,
          connection_name: globalConn?.connection_name || 'Unknown Connection',
          errorMessage: globalConn?.error_message || 'This connection has been deleted.'
        };
      }
      return null;
    }).filter(Boolean);
    const invalidatedMcpIds = new Set(invalidatedMcp.map(ic => ic.id));
    const validMcp = connections.filter(c => c.connection_status === 'valid' && !invalidatedMcpIds.has(c.id));

    // --- Partition Oqlous App Integrations ---
    const globalOqlousAppNames = new Set(oqlousTools.map(app => app.appname));
    const invalidatedOqlous = taskOqlousTools
      .filter(taskApp => !globalOqlousAppNames.has(taskApp.appname))
      .map(taskApp => ({
        appname: taskApp.appname,
        errorMessage: `The "${taskApp.appname}" integration is no longer authenticated or available.`
      }));
    const invalidatedOqlousAppNames = new Set(invalidatedOqlous.map(app => app.appname));
    const validOqlous = oqlousTools.filter(app => !invalidatedOqlousAppNames.has(app.appname));

    return {
      validConnectionsToShow: validMcp,
      invalidatedConnectionsToShow: invalidatedMcp,
      validOqlousAppsToShow: validOqlous,
      invalidatedOqlousAppsToShow: invalidatedOqlous
    };
  }, [connections, oqlousTools, taskMCPConnections, taskOqlousTools, isLoading]);

  // This function ONLY flips the boolean state, as requested.
  const handleManualSelectionToggle = () => {
    setTaskManualToolSelection(prev => !prev);
  };

  // --- MCP Connection Handlers ---
  const handleMcpExpansionToggle = (connectionId) => {
    setExpandedMcpId(prevId => (prevId === connectionId ? null : connectionId));
  };

  const handleMcpConnectionToggle = (e, connection) => {
    e.stopPropagation();
    const isAlreadyAdded = taskMCPConnections.some(tc => tc.id === connection.id);
    if (isAlreadyAdded) {
      setTaskMCPConnections(prev => prev.filter(tc => tc.id !== connection.id));
      if (expandedMcpId === connection.id) setExpandedMcpId(null);
    } else {
      setTaskMCPConnections(prev => [...prev, { id: connection.id, enabled_tool_names: connection.tools.map(tool => tool.name) }]);
    }
  };

  const handleMcpToolToggle = (connectionId, toolName) => {
    setTaskMCPConnections(prev => prev.map(conn => {
      if (conn.id === connectionId) {
        const enabledTools = new Set(conn.enabled_tool_names);
        enabledTools.has(toolName) ? enabledTools.delete(toolName) : enabledTools.add(toolName);
        return { ...conn, enabled_tool_names: Array.from(enabledTools) };
      }
      return conn;
    })
    );
  };

  // --- Oqlous App Handlers ---
  const handleOqlousAppExpansionToggle = (appname) => {
    setExpandedAppName(prevApp => (prevApp === appname ? null : appname));
  };

  const handleOqlousAppToggle = (e, app) => {
    e.stopPropagation();
    const isAlreadyAdded = taskOqlousTools.some(ta => ta.appname === app.appname);
    if (isAlreadyAdded) {
      setTaskOqlousTools(prev => prev.filter(ta => ta.appname !== app.appname));
      if (expandedAppName === app.appname) setExpandedAppName(null);
    } else {
      setTaskOqlousTools(prev => [...prev, { appname: app.appname, enabled_tool_names: app.tools.map(tool => tool.name) }]);
    }
  };

  const handleOqlousToolToggle = (appname, toolName) => {
    setTaskOqlousTools(prev => prev.map(app => {
      if (app.appname === appname) {
        const enabledTools = new Set(app.enabled_tool_names);
        enabledTools.has(toolName) ? enabledTools.delete(toolName) : enabledTools.add(toolName);
        return { ...app, enabled_tool_names: Array.from(enabledTools) };
      }
      return app;
    })
    );
  };

  return (
    <>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-[110] bg-black bg-opacity-50" onClick={onClose}></div>
          <div className="fixed top-1/2 left-1/2 z-[120] w-[90vw] max-w-3xl max-h-[80vh] transform -translate-x-1/2 -translate-y-1/2 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl flex flex-col">
             <header className="flex items-center justify-between p-4 border-b border-zinc-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <CgPlug className="text-blue-400 text-xl" />
                <h3 className="text-lg font-semibold text-white">Select Tools for Task</h3>
              </div>

              {/* NEW: Toggle is now in the header */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2" title="Enable/Disable Tool Editing">
                  {/* <span className="text-xs text-gray-400">Edit</span> */}
                  <ToggleSwitch 
                    enabled={taskManualToolSelection} 
                    onChange={handleManualSelectionToggle} 
                  />
                </div>
                <button onClick={onClose} className="p-1 rounded-full hover:bg-zinc-700">
                    <IoMdClose size={20} />
                </button>
              </div>
            </header>

            <div className="flex-1 p-6 overflow-y-auto space-y-6">
              
            {/* --- NEW: Wrapper Div to Conditionally Disable UI --- */}
              <div className={`transition-opacity space-y-6 duration-300 ${!taskManualToolSelection ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
              {isLoading ? (
                <p className="text-center text-gray-400">Loading tools and connections...</p>
              ) : (
                <>


                  {/* --- MCP CONNECTIONS SECTION --- */}
                  <div>
                    <h3 className="text-base font-bold text-white mb-3">MCP Connections</h3>
                    <div className="space-y-2">
                      {validConnectionsToShow.map(conn => {
                        const taskConn = taskMCPConnections.find(tc => tc.id === conn.id);
                        const isConnectionEnabled = !!taskConn;
                        const isExpanded = expandedMcpId === conn.id;
                        return (
                          <div key={conn.id} className="bg-zinc-800/50 rounded-lg border border-zinc-700">
                            <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-zinc-800" onClick={() => isConnectionEnabled && handleMcpExpansionToggle(conn.id)}>
                              <p className="font-semibold text-white">{conn.connection_name}</p>
                              <div className="flex items-center gap-4">
                                <ToggleSwitch enabled={isConnectionEnabled} onChange={(e) => handleMcpConnectionToggle(e, conn)} />
                                {isConnectionEnabled && <span className="text-gray-400">{isExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>}
                              </div>
                            </div>
                            {isConnectionEnabled && isExpanded && (
                              <div className="p-4 border-t border-zinc-700 space-y-3">
                                {conn.tools.map(tool => {
                                  const isToolEnabled = taskConn.enabled_tool_names.includes(tool.name);
                                  return (
                                    <div key={tool.name} className="flex items-center justify-between pl-2">
                                      <div>
                                        <p className="text-sm text-gray-200">{tool.name}</p>
                                        <p className="text-xs text-gray-500 max-w-lg truncate">{tool.description}</p>
                                      </div>
                                      <ToggleSwitch enabled={isToolEnabled} onChange={() => handleMcpToolToggle(conn.id, tool.name)} />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* --- OQLOUS TOOLS SECTION --- */}
                  <div>
                    <h3 className="text-base font-bold text-white mb-3">Oqlous App Integrations</h3>
                    <div className="space-y-2">
                      {validOqlousAppsToShow.map(app => {
                        const taskApp = taskOqlousTools.find(ta => ta.appname === app.appname);
                        const isAppEnabled = !!taskApp;
                        const isExpanded = expandedAppName === app.appname;

                        return (
                          <div key={app.appname} className="bg-zinc-800/50 rounded-lg border border-zinc-700">
                            <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-zinc-800" onClick={() => isAppEnabled && handleOqlousAppExpansionToggle(app.appname)}>
                              <p className="font-semibold text-white">{app.label || app.appname}</p>
                              <div className="flex items-center gap-4">
                                <ToggleSwitch enabled={isAppEnabled} onChange={(e) => handleOqlousAppToggle(e, app)} />
                                {isAppEnabled && <span className="text-gray-400">{isExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>}
                              </div>
                            </div>
                            {isAppEnabled && isExpanded && (
                              <div className="p-4 border-t border-zinc-700 space-y-3">
                                {app.tools.map(tool => {
                                  const isToolEnabled = taskApp.enabled_tool_names.includes(tool.name);
                                  return (
                                    <div key={tool.name} className="flex items-center justify-between pl-2">
                                      <div>
                                        <p className="text-sm text-gray-200">{tool.name}</p>
                                        <p className="text-xs text-gray-500 max-w-lg truncate">{tool.description}</p>
                                      </div>
                                      <ToggleSwitch enabled={isToolEnabled} onChange={() => handleOqlousToolToggle(app.appname, tool.name)} />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* --- UNIFIED "ATTENTION REQUIRED" SECTION --- */}
                  {(invalidatedConnectionsToShow.length > 0 || invalidatedOqlousAppsToShow.length > 0) && (
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-yellow-400 mb-2 flex items-center gap-2"><FiAlertTriangle /> Attention Required</h4>
                      <p className="text-xs text-gray-400 mb-4">The following items were selected for this task but are now invalid or disabled. You can remove them from this task's configuration.</p>
                      <div className="space-y-2">
                        {/* Render Invalidated Oqlous Apps */}
                        {invalidatedOqlousAppsToShow.map(app => (
                          <div key={app.appname} className="bg-red-900/20 rounded-lg border border-red-500/30 p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-white line-through">{app.appname}</p>
                                <p className="text-xs text-red-400">{app.errorMessage}</p>
                              </div>
                              <ToggleSwitch enabled={true} onChange={(e) => handleOqlousAppToggle(e, app)} />
                            </div>
                          </div>
                        ))}
                        {/* Render Invalidated MCP Connections */}
                        {invalidatedConnectionsToShow.map(conn => (
                          <div key={conn.id} className="bg-red-900/20 rounded-lg border border-red-500/30 p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-white line-through">{conn.connection_name}</p>
                                <p className="text-xs text-red-400">{conn.errorMessage}</p>
                              </div>
                              <ToggleSwitch enabled={true} onChange={(e) => handleMcpConnectionToggle(e, conn)} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {validConnectionsToShow.length === 0 && validOqlousAppsToShow.length === 0 && invalidatedConnectionsToShow.length === 0 && invalidatedOqlousAppsToShow.length === 0 && (
                    <p className="text-center text-gray-500">No tools or connections found. Add integrations or MCP connections first.</p>
                  )}
                </>
              )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default McpTaskSelector;